/**
 * WorkspaceWidgets.js
 *
 * Renderers of the workspace dashboard. One widget instance -> one of six
 * renderers (metric / grid / trend / breakdown / list / actions) driven by the
 * catalogue definition and the real `main-dashboard/summary` payload, plus the
 * quiet states every card can be in: skeleton, empty, locked (module revoked),
 * error (retry) and "unsupported" (a widget type this build cannot draw - shown
 * as an "open in admin" card, never a crash).
 *
 * Reuses the app's premium kit: `PremiumStatCard` (KPI tile with a faint
 * background chart), `Sparkline`, `DeltaPill`. Sizes: S = half width, M = full,
 * L = full and taller / more rows.
 *
 * @module components/workspaces/WorkspaceWidgets
 */

import React from 'react';
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import PremiumStatCard from '../PremiumStatCard';
import {DeltaPill, Sparkline} from '../premium';
import {useManagementTheme} from '../../helpers/managementTheme';
import {WORKSPACE_MODULES, resolveWidgetDefinition} from '../../config/workspaceCatalog';
import {
    WIDGET_STATUS,
    formatKpi,
    formatMoney,
    formatShortDate,
    formatUnitValue,
    resolveWidgetView,
} from '../../helpers/workspaces/workspaceData';
import {ADMIN_DASHBOARD_PATH, WidgetCard, WidgetMessage, WidgetSkeleton, openInAdmin, useWorkspaceLabels} from './WorkspaceUI';

/** Series colours for breakdown bars (same ramp as the admin charts). */
const PALETTE = ['#F97316', '#3B82F6', '#16A34A', '#8B5CF6', '#F59E0B', '#14B8A6', '#EC4899', '#64748B'];

/** Priority dot colours of the action rows. */
const PRIORITY_COLOR = {critical: '#DC2626', high: '#F59E0B', normal: '#3B82F6'};

/** Rows shown per size. */
const ROWS_BY_SIZE = {S: 3, M: 5, L: 8};

// Width is owned by the screen's item wrapper (S = half, M/L = full); every card fills it.
const widthOf = () => '100%';

const moduleVisual = (module) => WORKSPACE_MODULES[module] || {icon: 'view-dashboard-outline', tone: 'primary'};

/** KPI / grid tile via the shared premium stat card. */
const StatTile = ({definition, kpi, title, delta, values, width, seed, invert}) => {
    const {i18n} = useTranslation();
    const visual = moduleVisual(definition.module);

    return (
        <PremiumStatCard
            icon={visual.icon}
            iconSet="community"
            tone={visual.tone === 'neutral' ? 'slate' : visual.tone}
            title={title}
            value={formatKpi(kpi, i18n.language)}
            delta={delta && !invert ? delta.pct : undefined}
            chartData={values && values.length > 1 ? values : undefined}
            chartKind="line"
            seed={seed}
            width={width}
        />
    );
};

const MetricWidget = ({definition, view, size, title}) => {
    const m = useManagementTheme();
    const {t, i18n} = useTranslation();
    const visual = moduleVisual(definition.module);

    if (definition.progress && view.kpi.unit === 'percent') {
        const pct = Math.max(0, Math.min(100, view.kpi.value));
        const bad = definition.dangerAt ? view.kpi.value >= definition.dangerAt : false;
        const warn = definition.warnAt ? view.kpi.value >= definition.warnAt : false;
        const color = bad ? m.colors.danger : (warn ? m.colors.warning : m.colors.primary);

        return (
            <WidgetCard title={title} icon={visual.icon} tone={visual.tone} width={widthOf(size)}>
                <Text style={[styles.bigValue, {color: m.colors.heading}]}>{formatKpi(view.kpi, i18n.language)}</Text>
                <View style={[styles.track, {backgroundColor: m.colors.surfaceAlt}]}>
                    <View style={[styles.fill, {width: `${pct}%`, backgroundColor: color}]} />
                </View>
            </WidgetCard>
        );
    }

    if (size === 'L') {
        return (
            <WidgetCard
                title={title}
                icon={visual.icon}
                tone={visual.tone}
                width="100%"
                right={view.delta ? <DeltaPill delta={view.delta.pct} /> : null}
            >
                <Text style={[styles.bigValue, {color: m.colors.heading}]}>{formatKpi(view.kpi, i18n.language)}</Text>
                {view.values.length > 1 && <Sparkline data={view.values} height={96} dotRing={m.colors.surface} />}
                {typeof view.kpi.previous === 'number' && (
                    <Text style={[styles.caption, {color: m.colors.subText}]}>
                        {t('wsd_previous_period', {value: formatKpi({...view.kpi, value: view.kpi.previous}, i18n.language)})}
                    </Text>
                )}
            </WidgetCard>
        );
    }

    return (
        <View style={styles.tileWrap}>
            <StatTile
                definition={definition}
                kpi={view.kpi}
                title={title}
                delta={view.delta}
                values={view.values}
                width={widthOf(size)}
                seed={definition.id.length}
                invert={definition.invertDelta}
            />
        </View>
    );
};

const GridWidget = ({definition, view, size}) => {
    const labels = useWorkspaceLabels();
    const limit = size === 'S' ? 1 : (size === 'L' ? 6 : 4);

    return (
        <View style={styles.tileRow}>
            {view.tiles.slice(0, limit).map((tile, index) => (
                <StatTile
                    key={tile.key}
                    definition={definition}
                    kpi={tile.kpi}
                    title={labels.kpi(definition.module, tile.key)}
                    delta={tile.delta}
                    width={size === 'S' ? '100%' : '48.5%'}
                    seed={index + definition.id.length}
                    invert={tile.invert}
                />
            ))}
        </View>
    );
};

const TrendWidget = ({definition, view, size, title}) => {
    const m = useManagementTheme();
    const {i18n} = useTranslation();
    const visual = moduleVisual(definition.module);
    const previousTotal = view.compare.reduce((a, b) => a + b, 0);
    const delta = previousTotal > 0 ? ((view.total - previousTotal) / previousTotal) * 100 : null;

    return (
        <WidgetCard
            title={title}
            icon={visual.icon}
            tone={visual.tone}
            width={widthOf(size)}
            right={size !== 'S' && delta !== null ? <DeltaPill delta={delta} /> : null}
        >
            <Text style={[styles.bigValue, size === 'S' && styles.bigValueSmall, {color: m.colors.heading}]} numberOfLines={1}>
                {formatUnitValue(view.total, view.series, i18n.language)}
            </Text>
            <Sparkline data={view.values} height={size === 'L' ? 150 : (size === 'S' ? 40 : 84)} dotRing={m.colors.surface} />
        </WidgetCard>
    );
};

const BreakdownWidget = ({definition, view, size, title, options}) => {
    const m = useManagementTheme();
    const labels = useWorkspaceLabels();
    const {i18n} = useTranslation();
    const visual = moduleVisual(definition.module);
    const limit = options?.limit || ROWS_BY_SIZE[size];

    return (
        <WidgetCard title={title} icon={visual.icon} tone={visual.tone} width={widthOf(size)}>
            {view.rows.slice(0, limit).map((row, index) => (
                <View key={row.key} style={styles.bdRow}>
                    <View style={styles.bdHead}>
                        <View style={[styles.dot, {backgroundColor: PALETTE[index % PALETTE.length]}]} />
                        <Text style={[styles.bdLabel, {color: m.colors.text}]} numberOfLines={1}>{labels.term(row.label, row.label)}</Text>
                        {size !== 'S' && (
                            <Text style={[styles.bdValue, {color: m.colors.heading}]} numberOfLines={1}>
                                {formatUnitValue(row.value, view.breakdown, i18n.language)}
                            </Text>
                        )}
                    </View>
                    <View style={[styles.track, {backgroundColor: m.colors.surfaceAlt}]}>
                        <View style={[styles.fill, {width: `${Math.max(4, Math.round(row.share * 100))}%`, backgroundColor: PALETTE[index % PALETTE.length]}]} />
                    </View>
                </View>
            ))}
        </WidgetCard>
    );
};

const ListWidget = ({definition, view, size, title, options, navigation}) => {
    const m = useManagementTheme();
    const labels = useWorkspaceLabels();
    const visual = moduleVisual(definition.module || definition.lists?.[0]?.module);
    const limit = options?.limit || ROWS_BY_SIZE[size];

    return (
        <WidgetCard title={title} icon={visual.icon} tone={visual.tone} width={widthOf(size)}>
            {view.items.slice(0, limit).map((item, index) => {
                const sub = [item.subtitle, item.date ? formatShortDate(item.date, labels.language) : ''].filter(Boolean).join(' · ');

                return (
                    <TouchableOpacity
                        key={String(item.id || index)}
                        disabled={!item.link}
                        onPress={() => openInAdmin(navigation, item.link)}
                        activeOpacity={0.7}
                        style={[styles.listRow, index > 0 && {borderTopColor: m.colors.borderSoft, borderTopWidth: StyleSheet.hairlineWidth}]}
                    >
                        <View style={styles.listText}>
                            <Text style={[styles.listTitle, {color: m.colors.heading}]} numberOfLines={1}>{String(item.title ?? '')}</Text>
                            {!!sub && size !== 'S' && <Text style={[styles.listSub, {color: m.colors.subText}]} numberOfLines={1}>{sub}</Text>}
                        </View>
                        {typeof item.amount === 'number' && size !== 'S' && (
                            <Text style={[styles.listAmount, {color: m.colors.heading}]} numberOfLines={1}>
                                {formatMoney(item.amount, item.currency, labels.language)}
                            </Text>
                        )}
                        {!!item.status && size === 'L' && (
                            <Text style={[styles.listStatus, {color: m.colors.primary, backgroundColor: m.colors.chipBg}]} numberOfLines={1}>
                                {labels.term(item.status, item.status)}
                            </Text>
                        )}
                    </TouchableOpacity>
                );
            })}
        </WidgetCard>
    );
};

const ActionsWidget = ({definition, view, size, title, options, navigation}) => {
    const m = useManagementTheme();
    const labels = useWorkspaceLabels();
    const visual = moduleVisual(definition.module);
    const limit = options?.limit || ROWS_BY_SIZE[size];

    return (
        <WidgetCard title={title} icon="lightning-bolt-outline" tone={definition.module ? visual.tone : 'warning'} width={widthOf(size)}>
            {view.items.slice(0, limit).map((action, index) => (
                <TouchableOpacity
                    key={String(action.id || index)}
                    disabled={!action.link}
                    onPress={() => openInAdmin(navigation, action.link)}
                    activeOpacity={0.7}
                    style={[styles.listRow, index > 0 && {borderTopColor: m.colors.borderSoft, borderTopWidth: StyleSheet.hairlineWidth}]}
                >
                    <View style={[styles.dot, {backgroundColor: PRIORITY_COLOR[action.priority] || PRIORITY_COLOR.normal}]} />
                    <Text style={[styles.listTitle, styles.actionText, {color: m.colors.text}]} numberOfLines={size === 'S' ? 2 : 3}>
                        {labels.action(action, (amount, currency) => formatMoney(amount, currency, labels.language))}
                    </Text>
                </TouchableOpacity>
            ))}
        </WidgetCard>
    );
};

/**
 * One widget instance of a workspace.
 *
 * @param {object} props
 * @param {{id: string, type: string, size: string, options?: object}} props.widget Stored instance.
 * @param {?object} props.summary Summary of the instance's (store, period) request, `undefined` while loading.
 * @param {?object} props.error Request error (`{status}`) when the load failed and no summary is cached.
 * @param {() => void} props.onRetry Retry handler.
 * @returns {JSX.Element} The card.
 */
export default function WorkspaceWidget({widget, summary, error, onRetry}) {
    const {t} = useTranslation();
    const labels = useWorkspaceLabels();
    const navigation = useNavigation();
    const definition = resolveWidgetDefinition(widget.type);
    const title = labels.title(widget.type);
    const size = widget.size || 'M';
    const visual = moduleVisual(definition.module);

    if (definition.kind === 'unsupported') {
        return (
            <WidgetCard title={title} icon="monitor-dashboard" tone="neutral" width="100%">
                <WidgetMessage
                    icon="open-in-new"
                    text={t('wsd_unsupported_text')}
                    actionLabel={t('wsd_open_in_admin')}
                    onAction={() => openInAdmin(navigation, ADMIN_DASHBOARD_PATH)}
                />
            </WidgetCard>
        );
    }

    if (!summary) {
        return !error
            ? <WidgetSkeleton size={size} />
            : (
                <WidgetCard title={title} icon={visual.icon} tone={visual.tone} width={widthOf(size)}>
                    <WidgetMessage icon="cloud-off-outline" text={t('wsd_widget_error')} actionLabel={t('wsd_retry')} onAction={onRetry} tone="danger" />
                </WidgetCard>
            );
    }

    const {status, view} = resolveWidgetView(definition, summary, widget.options);

    if (status === WIDGET_STATUS.OK) {
        const props = {definition, view, size, title, options: widget.options, navigation};

        switch (definition.kind) {
            case 'metric': return <MetricWidget {...props} />;
            case 'grid': return <GridWidget {...props} />;
            case 'trend': return <TrendWidget {...props} />;
            case 'breakdown': return <BreakdownWidget {...props} />;
            case 'list': return <ListWidget {...props} />;
            case 'actions': return <ActionsWidget {...props} />;
            default: break;
        }
    }

    const message = {
        [WIDGET_STATUS.FORBIDDEN]: {icon: 'lock-outline', text: t('wsd_widget_locked'), tone: 'warning'},
        [WIDGET_STATUS.ERROR]: {icon: 'cloud-off-outline', text: t('wsd_widget_error'), tone: 'danger', retry: true},
        [WIDGET_STATUS.EMPTY]: {icon: 'chart-box-outline', text: t('wsd_widget_empty'), tone: 'neutral'},
    }[status] || {icon: 'help-circle-outline', text: t('wsd_widget_empty'), tone: 'neutral'};

    return (
        <WidgetCard title={title} icon={visual.icon} tone={visual.tone} width={widthOf(size)}>
            <WidgetMessage
                icon={message.icon}
                text={message.text}
                tone={message.tone}
                actionLabel={message.retry ? t('wsd_retry') : undefined}
                onAction={message.retry ? onRetry : undefined}
            />
        </WidgetCard>
    );
}

const styles = StyleSheet.create({
    tileWrap: {marginBottom: 12},
    tileRow: {flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', rowGap: 12, marginBottom: 12},
    bigValue: {fontSize: 28, fontWeight: '800', letterSpacing: -0.5, marginBottom: 6},
    bigValueSmall: {fontSize: 20},
    caption: {fontSize: 12, marginTop: 6},
    track: {height: 8, borderRadius: 4, overflow: 'hidden'},
    fill: {height: 8, borderRadius: 4},
    bdRow: {marginBottom: 10},
    bdHead: {flexDirection: 'row', alignItems: 'center', marginBottom: 5},
    dot: {width: 8, height: 8, borderRadius: 4, marginRight: 8},
    bdLabel: {flex: 1, fontSize: 13},
    bdValue: {fontSize: 13, fontWeight: '700', marginLeft: 8},
    listRow: {flexDirection: 'row', alignItems: 'center', paddingVertical: 9},
    listText: {flex: 1},
    listTitle: {fontSize: 14, fontWeight: '600'},
    actionText: {flex: 1, fontWeight: '500'},
    listSub: {fontSize: 12, marginTop: 2},
    listAmount: {fontSize: 13, fontWeight: '700', marginLeft: 8},
    listStatus: {fontSize: 11, fontWeight: '700', marginLeft: 8, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, overflow: 'hidden'},
});
